import { useState, useEffect } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import { ROLES } from 'utils/Constants';
import ScoreTabNavigation from './ScoreTabNavigation';
import DynamicScoreContent from './DynamicScoreContent';
import useConsultantScoreData from 'utils/hooks/useConsultantScoreData';
import AdminScoringSectionContainer from './AdminScoringSectionContainer';

import './scoring-section.css';

const ScoringSectionContainer = (props) => {
  const [activeTab, setActiveTab] = useState(props.defaultScoreTab || 'seo'); // 'global', 'seo', or 'ux'
  const [seoScoreEnabled, setSeoScoreEnabled] = useState(null);
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = useState(null);

  const modeLower = (props.mode || '').toLowerCase();
  const isAdminLike = modeLower === 'admin' || modeLower === 'adminclient';
  const isUiOnly = modeLower === 'ui';
  const skipCompanyQuotas =
    props.skipCompanyQuotas || props.userRole === ROLES.CONSULTANT;

  useEffect(() => {
    if (skipCompanyQuotas) {
      setSeoScoreEnabled(true);
      setGapAnalysisEnabled(true);
      return undefined;
    }

    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setSeoScoreEnabled(data?.seoScoreEnabled !== false);
        setGapAnalysisEnabled(data?.gapAnalysisEnabled !== false);
      } catch (error) {
        if (isMounted) {
          setSeoScoreEnabled(true);
          setGapAnalysisEnabled(true);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, [skipCompanyQuotas]);

  // Always call hooks in a stable order; neutralize when not needed
  const { scoreData, error } = useConsultantScoreData(
    isAdminLike || isUiOnly ? null : props.textId,
    isAdminLike || isUiOnly ? null : props.preCalculatedScoreData
  );

  if (isAdminLike) {
    return (
      <AdminScoringSectionContainer
        {...props}
        defaultScoreTab={props.defaultScoreTab}
        seoScoreEnabled={seoScoreEnabled !== false}
        gapAnalysisEnabled={gapAnalysisEnabled !== false}
      />
    );
  }


  if (isUiOnly) {
    const zeroScoreData = {
      globalScore: 0,
      seoScore: 0,
      uxScore: 0,
      industryAverage: 0,
      yourRanking: 0,
      competitors: [],
      keywordTracking: [],
      metrics: {
        semanticResult: {
          score: 0,
          infoTooltip: '',
          data: { wordsInBold: [], suggestions: [] },
        },
        tags: {
          score: 0,
          infoTooltip: '',
          data: {
            title: { score: 0, present: false },
            metaDescription: { score: 0, present: false },
            headings: { score: 0, h1: 0, h2: 0, h3: 0 },
            images: { score: 0, withAlt: 0, withoutAlt: 0 },
          },
        },
        keywordStuffing: {
          title: 'Keyword stuffing',
          score: 0,
          infoTooltip: '',
          data: [],
        },
        internalLinking: {
          score: 0,
          infoTooltip: '',
          data: [],
        },
        readability: {
          score: { score: 0 },
          infoTooltip: '',
        },
        structure: {
          title: 'Structure',
          score: 0,
          infoTooltip: '',
        },
      },
    };

    return (
      <div className={`scoring-section-container ${!seoScoreEnabled ? 'score-disabled' : ''}`}>
        <ScoreTabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
        <DynamicScoreContent
          activeTab={activeTab}
          scoreData={zeroScoreData}
          isEditing={props.isEditing}
          websites={props.websites}
          actualHtmlContent={props.actualHtmlContent}
          setIsEditing={props.setIsEditing}
          handleInputChange={props.handleInputChange}
          checkIfAllLinesGreen={props.checkIfAllLinesGreen}
          userRole={props.userRole}
          onGapUpdate={props.handleGapUpdate}
          isKeywordBoxReadOnly={props.isKeywordBoxReadOnly}
          combinedKeywords={props.combinedKeywords}
          handleKeywordChange={props.handleKeywordChange}
          orderContentType={props.orderContentType}
          autoKeywordsGenerationLoading={props.autoKeywordsGenerationLoading}
          isReadOnly={props.isKeywordBoxReadOnly}
          wordCount={props.wordCount}
          keyword={props.keyword}
          url={props.url}
          orderWebsite={props.orderWebsite}
          textId={props.textId}
          gapAnalysisEnabled={gapAnalysisEnabled !== false}
        />
        {!seoScoreEnabled && (
          <div className="score-disabled-overlay">
            <span>Score SEO desactive</span>
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="score-error-container">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Erreur lors du calcul du score:</strong>
          <br />
          {error.message || error || "Une erreur inattendue s'est produite."}
        </div>
      </div>
    );
  }

  return (
    <div className={`scoring-section-container ${!seoScoreEnabled ? 'score-disabled' : ''}`}>
      <ScoreTabNavigation activeTab={activeTab} setActiveTab={setActiveTab} />
      <DynamicScoreContent
        activeTab={activeTab}
        scoreData={
          scoreData && scoreData.metrics
            ? scoreData
            : {
                globalScore: 0,
                seoScore: 0,
                uxScore: 0,
                industryAverage: 0,
                yourRanking: 0,
                competitors: [],
                keywordTracking: [],
                metrics: {
                  semanticResult: {
                    score: 0,
                    infoTooltip: '',
                    data: { wordsInBold: [], suggestions: [] },
                  },
                  tags: {
                    score: 0,
                    infoTooltip: '',
                    data: {
                      title: { score: 0, present: false },
                      metaDescription: { score: 0, present: false },
                      headings: { score: 0, h1: 0, h2: 0, h3: 0 },
                      images: { score: 0, withAlt: 0, withoutAlt: 0 },
                    },
                  },
                  keywordStuffing: {
                    title: 'Keyword stuffing',
                    score: 0,
                    infoTooltip: '',
                    data: [],
                  },
                  internalLinking: {
                    score: 0,
                    infoTooltip: '',
                    data: [],
                  },
                  readability: {
                    score: { score: 0 },
                    infoTooltip: '',
                  },
                  structure: {
                    title: 'Structure',
                    score: 0,
                    infoTooltip: '',
                  },
                },
              }
        }
        isEditing={props.isEditing}
        websites={props.websites}
        actualHtmlContent={props.actualHtmlContent}
        setIsEditing={props.setIsEditing}
        handleInputChange={props.handleInputChange}
        checkIfAllLinesGreen={props.checkIfAllLinesGreen}
        userRole={props.userRole}
        onGapUpdate={props.handleGapUpdate}
        isKeywordBoxReadOnly={props.isKeywordBoxReadOnly}
        combinedKeywords={props.combinedKeywords}
        handleKeywordChange={props.handleKeywordChange}
        orderContentType={props.orderContentType}
        autoKeywordsGenerationLoading={props.autoKeywordsGenerationLoading}
        isReadOnly={props.isKeywordBoxReadOnly}
        wordCount={props.wordCount}
        keyword={props.keyword}
        url={props.url}
        orderWebsite={props.orderWebsite}
        textId={props.textId}
        gapAnalysisEnabled={gapAnalysisEnabled}
      />
      {!seoScoreEnabled && (
        <div className="score-disabled-overlay">
          <span>Score SEO desactive</span>
        </div>
      )}
    </div>
  );
};

export default ScoringSectionContainer;
